import { initials } from "../misc/initials";
import { useGetOne } from "ra-core";

import { cn } from "@/lib/utils";
import type { Sale } from "../types";

// Sprint 8 — Avatar membre équipe avec palette dédiée Jules/Théo/Faycal
// (cf maquette cal-shared.jsx MEMBERS + memory project_atomic_crm_accounts).
// Pour les autres sales (futures) : fallback palette pastel hash sur initiales.

const MEMBER_PALETTE: Record<
  number,
  { bg: string; fg: string; dark_bg: string; dark_fg: string }
> = {
  1: {
    // Jules — orange
    bg: "#fed7aa",
    fg: "#9a3412",
    dark_bg: "rgba(154, 52, 18, 0.4)",
    dark_fg: "#fdba74",
  },
  2: {
    // Théo — emerald
    bg: "#d1fae5",
    fg: "#065f46",
    dark_bg: "rgba(6, 95, 70, 0.4)",
    dark_fg: "#6ee7b7",
  },
  3: {
    // Faycal — red
    bg: "#fecaca",
    fg: "#991b1b",
    dark_bg: "rgba(153, 27, 27, 0.4)",
    dark_fg: "#fca5a5",
  },
};

const FALLBACK_PASTEL = [
  { bg: "#f5d3a4", fg: "#6e3a13" }, // peach
  { bg: "#c9e7d8", fg: "#185c41" }, // mint
  { bg: "#cfdef9", fg: "#2d4d96" }, // sky
  { bg: "#ddd1f3", fg: "#4a3892" }, // lav
  { bg: "#f3cad2", fg: "#8b2c44" }, // pink
  { bg: "#ecdfb5", fg: "#6f5b1a" }, // cream
] as const;

function pickFallback(key: string) {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return FALLBACK_PASTEL[hash % FALLBACK_PASTEL.length];
}

interface MemberAvatarProps {
  salesId: number | string | null | undefined;
  size?: number;
  className?: string;
  title?: string;
}

export const MemberAvatar = ({
  salesId,
  size = 28,
  className,
  title,
}: MemberAvatarProps) => {
  const enabled = salesId != null;
  const { data: sale } = useGetOne<Sale>(
    "sales",
    { id: salesId ?? 0 },
    { enabled },
  );

  if (!enabled) {
    return (
      <div
        className={cn(
          "rounded-full flex items-center justify-center bg-muted text-muted-foreground font-semibold",
          className,
        )}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
        title={title ?? "Non assigné"}
      >
        ?
      </div>
    );
  }

  const numId = Number(salesId);
  const knownPalette = MEMBER_PALETTE[numId];

  const label = sale ? initials(sale) : "—";

  const fallbackPalette = pickFallback(String(salesId));
  const palette = knownPalette ?? {
    bg: fallbackPalette.bg,
    fg: fallbackPalette.fg,
    dark_bg: fallbackPalette.bg,
    dark_fg: fallbackPalette.fg,
  };

  const displayTitle =
    title ??
    (sale
      ? `${sale.first_name ?? ""} ${sale.last_name ?? ""}`.trim()
      : `Sales #${salesId}`);

  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center font-semibold shrink-0",
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.36),
        backgroundColor: palette.bg,
        color: palette.fg,
      }}
      title={displayTitle}
    >
      {label}
    </div>
  );
};
